"use client";

// TODO: Investigate why autocomplete is not working in production.
// Potential reasons:
// 1. the API key might be missing in the production environment variables (VITE_GOOGLE_MAPS_API_KEY).
// 2. The Places API might not be enabled for this specific API key in GCP.
// 3. The API key might have HTTP referrer restrictions that block the production domain.

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import React, { useEffect, useRef, useState } from "react";

// Google Maps types
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

export interface AddressComponents {
  street?: string;
  streetNumber?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  country?: string;
  formattedAddress: string;
}

interface AddressAutocompleteProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange: (addressComponents: AddressComponents) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  name?: string;
  id?: string;
}

export function AddressAutocomplete({
  label = "Adresse",
  placeholder = "Adresse eingeben",
  value = "",
  onChange,
  disabled = false,
  required = false,
  className,
  name = "address",
  id
}: AddressAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [isLoaded, setIsLoaded] = useState(false);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }

    // Check if script is already loading
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps) {
          setIsLoaded(true);
          clearInterval(checkGoogle);
        }
      }, 100);
      return () => clearInterval(checkGoogle);
    }

    // Load the script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places&language=de&region=DE`;
    script.async = true;
    script.defer = true;

    window.initMap = () => {
      setIsLoaded(true);
    };

    script.onload = () => {
      if (window.google && window.google.maps) {
        setIsLoaded(true);
      }
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
      window.initMap = () => {};
    };
  }, []);

  // Initialize services when Google Maps is loaded
  useEffect(() => {
    if (!isLoaded) return;

    autocompleteServiceRef.current =
      new window.google.maps.places.AutocompleteService();

    // Create a dummy div for PlacesService
    const dummyDiv = document.createElement("div");
    placesServiceRef.current = new window.google.maps.places.PlacesService(
      dummyDiv
    );
  }, [isLoaded]);

  // Handle input changes and fetch predictions
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);

    // Propagate the literal currently typed string to onChange so it is persisted
    // even if the user never selects an auto-completed value from the dropdown.
    onChange({
      formattedAddress: newValue
    });

    // If user clears the input, reset everything
    if (!newValue.trim()) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    // Fetch predictions from Google Places
    if (autocompleteServiceRef.current && newValue.length > 2) {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: newValue,
          types: ["address"],
          componentRestrictions: { country: "DE" }
        },
        (predictions: any[], status: any) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setPredictions(predictions.slice(0, 5)); // Limit to 5 results
            setShowDropdown(true);
          } else {
            setPredictions([]);
            setShowDropdown(false);
          }
        }
      );
    } else {
      setPredictions([]);
      setShowDropdown(false);
    }
  };

  // Handle place selection
  const selectPlace = (placeId: string, description: string) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId,
        fields: ["address_components", "formatted_address", "geometry", "name"]
      },
      (place: any, status: any) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          const components: AddressComponents = {
            formattedAddress: place.formatted_address || description
          };

          // Parse address components
          if (place.address_components) {
            place.address_components.forEach((component: any) => {
              const types = component.types;

              if (types.includes("street_number")) {
                components.streetNumber = component.long_name;
              }
              if (types.includes("route")) {
                components.street = component.long_name;
              }
              if (types.includes("postal_code")) {
                components.postalCode = component.long_name;
              }
              if (types.includes("locality")) {
                components.city = component.long_name;
              }
              if (types.includes("administrative_area_level_1")) {
                components.state = component.long_name;
              }
              if (types.includes("country")) {
                components.country = component.long_name;
              }
            });
          }

          setInputValue(components.formattedAddress);
          onChange(components);
          setShowDropdown(false);
          setPredictions([]);
        }
      }
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          const selected = predictions[selectedIndex];
          selectPlace(selected.place_id, selected.description);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setPredictions([]);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setPredictions([]);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <div className="space-y-2 relative">
      {label && <Label htmlFor={id || name}>{label}</Label>}
      <div className="relative">
        <Input
          ref={inputRef}
          id={id || name}
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={isLoaded ? placeholder : "Google Maps wird geladen..."}
          disabled={disabled || !isLoaded}
          required={required}
          className={cn(className)}
          autoComplete="off"
        />

        {/* Dropdown */}
        {showDropdown && predictions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
          >
            {predictions.map((prediction, index) => (
              <div
                key={prediction.place_id}
                className={cn(
                  "px-3 py-2 cursor-pointer text-sm hover:bg-gray-100",
                  selectedIndex === index && "bg-gray-100"
                )}
                onClick={() =>
                  selectPlace(prediction.place_id, prediction.description)
                }
              >
                <div className="font-medium">
                  {prediction.structured_formatting?.main_text}
                </div>
                <div className="text-gray-500 text-xs">
                  {prediction.structured_formatting?.secondary_text}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isLoaded && (
        <p className="text-xs text-muted-foreground">
          Laden der Adresssuche...
        </p>
      )}
    </div>
  );
}
